import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { captureException } from '@sentry/react';

import callApi, { METHODS, PATHS } from 'api';
import { selectUser } from 'features/User/selectors';

interface DeleteAccountPayload {
  feedback: string;
  type: string;
}

export const useDeleteAccount = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useSelector(selectUser);

  const deleteAccount = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setError('User ID not found');
      return false;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const payload: DeleteAccountPayload = {
        feedback: `DELETE_ACCOUNT ${user.id}`,
        type: 'feedback'
      };

      await callApi({
        method: METHODS.POST,
        mainPath: PATHS.USER_FEEDBACK,
        data: payload,
        authorized: true,
      });

      // Success - return true to trigger logout
      return true;
    } catch (error) {
      captureException(error);
      setError('There was an error deleting your account. Please try again.');
      setIsDeleting(false);
      return false;
    }
  }, [user?.id]);

  return {
    deleteAccount,
    isDeleting,
    error,
    clearError: () => setError(null),
  };
};